import React, { useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import InputWithDropdown from '../input-with-dropdown/input-with-dropdown';

const InputEventTypes = {
  DELETE_BACKWARD: 'deleteContentBackward',
  DELETE_FORWARD: 'deleteContentForward',
  INSERT: 'insertText',
  PASTE: 'insertFromPaste',
};
const Positions = {
  hours: 'hours',
  mins: 'mins',
};
export const MeridiemTimeIds = {
  AM: 'AM',
  PM: 'PM',
};
export const MeridiemTimeOptions = [{ id: MeridiemTimeIds.AM, label: 'AM' },
  { id: MeridiemTimeIds.PM, label: 'PM' }];

const TimeInput = (props) => {
  const {
    onChange, value, meridiemTime, meridiemOptions, ...rest
  } = props;

  const dataRef = useRef({
    hours: null, mins: null, position: Positions.hours, allowUpdate: true,
  });

  const dropdownItems = useMemo(
    () => meridiemOptions.map((option) => ({ ...option, selected: option.id === meridiemTime })),
    [meridiemOptions, meridiemTime],
  );

  useEffect(() => {
    const { current: { allowUpdate } } = dataRef;
    if (value && allowUpdate) {
      const [hours, mins] = value?.split(':') || [];
      dataRef.current.hours = parseInt(hours, 10);
      dataRef.current.mins = parseInt(mins, 10);
      dataRef.current.allowUpdate = false;
      dataRef.current.position = Positions.mins;
    }
  }, [value]);

  const formatTime = () => {
    const { hours, mins } = dataRef.current;
    let formattedTime = '';
    if (hours != null) {
      const formattedHours = hours < 10 ? `0${hours}` : hours;
      formattedTime += formattedHours;
    }
    if (mins != null) {
      const formattedMins = mins < 10 ? `0${mins}` : mins;
      formattedTime = `${formattedTime}:${formattedMins}`;
    }
    return formattedTime;
  };

  const removeNonDigits = (string) => string.replace(/[^0-9:]/g, '');

  const handleInsert = (event) => {
    const { target, data } = event;
    const { hours, mins, position } = dataRef.current;
    const dataAsInt = parseInt(data, 10);
    if (Number.isNaN(dataAsInt)) {
      target.value = removeNonDigits(target.value);
      return;
    }
    switch (position) {
      case Positions.hours:
        if (hours != null) {
          if (hours * 10 + dataAsInt <= 12) {
            dataRef.current.hours = hours * 10 + dataAsInt;
            dataRef.current.position = Positions.mins;
          } else {
            dataRef.current.mins = dataAsInt;
            dataRef.current.position = Positions.mins;
          }
        } else {
          dataRef.current.hours = dataAsInt;
        }
        break;
      case Positions.mins:
        if (mins != null) {
          if (mins > 10) break;
          if (mins * 10 + dataAsInt < 60) {
            dataRef.current.mins = mins * 10 + dataAsInt;
          }
        } else {
          dataRef.current.mins = dataAsInt;
        }
        break;
      default:
        break;
    }
    target.value = formatTime();
  };

  const handleDelete = (event) => {
    const { target } = event;
    const { position } = dataRef.current;
    switch (position) {
      case Positions.hours:
        dataRef.current.hours = null;
        break;
      case Positions.mins:
        dataRef.current.mins = null;
        dataRef.current.position = Positions.hours;
        break;
      default:
        break;
    }
    target.value = formatTime();
  };

  const handleInputEvent = (event) => {
    dataRef.current.allowUpdate = false;
    const { inputType, target } = event.nativeEvent;
    switch (inputType) {
      case InputEventTypes.INSERT:
        handleInsert(event.nativeEvent);
        break;
      case InputEventTypes.DELETE_BACKWARD:
      case InputEventTypes.DELETE_FORWARD:
        handleDelete(event.nativeEvent);
        break;
      case InputEventTypes.PASTE:
        target.value = formatTime();
        break;
      default: break;
    }
    onChange(target.value, meridiemTime);
  };
  const handleDropdownItemClick = (id) => {
    onChange(value, id);
  };
  return (
    <InputWithDropdown
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...rest}
      onInput={handleInputEvent}
      value={value}
      dropdownItems={dropdownItems}
      onDropdownItemSelect={handleDropdownItemClick}
    />
  );
};
TimeInput.defaultProps = {
  onChange: () => {},
  value: '',
  meridiemTime: MeridiemTimeIds.AM,
  meridiemOptions: MeridiemTimeOptions,
};
TimeInput.propTypes = {
  onChange: PropTypes.func,
  value: PropTypes.string,
  meridiemOptions: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    label: PropTypes.string,
  })),
  meridiemTime: PropTypes.oneOf(MeridiemTimeIds),
};

export default TimeInput;
